import {
  AcordAppendType,
  AcordFormConfigurationInput,
  AcordFormSection,
  AcordFormType,
  AcordNumberFormat,
  AcordProducerInformationSource,
  AgencyFieldsFragment,
  DescriptionOfOperationsMode
} from '~/api/graphql';

// Every form Cara issues carries a referenced-document list, so every one of
// them can host append rules.
export type AppendFormType = AcordFormType;

export const APPEND_FORMS: AppendFormType[] = [
  AcordFormType.Acord_20,
  AcordFormType.Acord_21,
  AcordFormType.Acord_22,
  AcordFormType.Acord_23,
  AcordFormType.Acord_24,
  AcordFormType.Acord_25,
  AcordFormType.Acord_27,
  AcordFormType.Acord_28,
  AcordFormType.Acord_29,
  AcordFormType.Acord_30
];

// The append categories each form can meaningfully claim, mirroring the backend
// `model.ACORDFormAppendTypes`. A category is offered only where the form prints
// something that refers to it: the ACORD 24 names no endorsement at all, the
// ACORD 28 waives subrogation but never names an additional insured, and the
// evidence forms carry an ADDITIONAL INTEREST block where the certificates carry
// an ADDITIONAL INSURED / SUBROGATION WAIVED pair. Offering the rest would let an
// agency tick a box that cannot change what comes out of the printer.
//
// NEVER_APPEND is not listed: it is how the setting says "nothing" rather than a
// category of page, so appendTypeOptionsForForm adds it to every form.
export const APPEND_TYPES_BY_FORM: Record<AppendFormType, AcordAppendType[]> = {
  [AcordFormType.Acord_20]: [
    AcordAppendType.AdditionalInsuredEndorsements,
    AcordAppendType.WaiverOfSubrogation,
    AcordAppendType.OtherReferencedEndorsements,
    AcordAppendType.DeclarationPagesAndSchedules
  ],
  [AcordFormType.Acord_21]: [
    AcordAppendType.AdditionalInsuredEndorsements,
    AcordAppendType.WaiverOfSubrogation,
    AcordAppendType.OtherReferencedEndorsements,
    AcordAppendType.DeclarationPagesAndSchedules
  ],
  [AcordFormType.Acord_22]: [
    AcordAppendType.AdditionalInsuredEndorsements,
    AcordAppendType.OtherReferencedEndorsements,
    AcordAppendType.DeclarationPagesAndSchedules
  ],
  [AcordFormType.Acord_23]: [
    AcordAppendType.AdditionalInsuredEndorsements,
    AcordAppendType.AdditionalInterestsLossPayees,
    AcordAppendType.OtherReferencedEndorsements,
    AcordAppendType.DeclarationPagesAndSchedules
  ],
  [AcordFormType.Acord_24]: [
    AcordAppendType.OtherReferencedEndorsements,
    AcordAppendType.DeclarationPagesAndSchedules
  ],
  [AcordFormType.Acord_25]: [
    AcordAppendType.AdditionalInsuredEndorsements,
    AcordAppendType.WaiverOfSubrogation,
    AcordAppendType.OtherReferencedEndorsements,
    AcordAppendType.DeclarationPagesAndSchedules
  ],
  [AcordFormType.Acord_27]: [
    AcordAppendType.AdditionalInsuredEndorsements,
    AcordAppendType.AdditionalInterestsLossPayees,
    AcordAppendType.OtherReferencedEndorsements,
    AcordAppendType.DeclarationPagesAndSchedules
  ],
  [AcordFormType.Acord_28]: [
    AcordAppendType.AdditionalInterestsLossPayees,
    AcordAppendType.WaiverOfSubrogation,
    AcordAppendType.OtherReferencedEndorsements,
    AcordAppendType.DeclarationPagesAndSchedules
  ],
  [AcordFormType.Acord_29]: [
    AcordAppendType.AdditionalInsuredEndorsements,
    AcordAppendType.AdditionalInterestsLossPayees,
    AcordAppendType.OtherReferencedEndorsements,
    AcordAppendType.DeclarationPagesAndSchedules
  ],
  [AcordFormType.Acord_30]: [
    AcordAppendType.AdditionalInsuredEndorsements,
    AcordAppendType.WaiverOfSubrogation,
    AcordAppendType.OtherReferencedEndorsements,
    AcordAppendType.DeclarationPagesAndSchedules
  ]
};

// The options the selector offers for a form: the categories it can claim, plus
// "Never append".
export const appendTypeOptionsForForm = (form: AppendFormType): AcordAppendType[] => [
  ...APPEND_TYPES_BY_FORM[form],
  AcordAppendType.NeverAppend
];

// Forms that can host section-scoped rules. ACORD 28 has no scoped sections in
// this release, so it is excluded from the rules editor (the backend rejects a
// rule whose section does not belong to its form).
export const RULE_FORMS: AcordFormType[] = [
  AcordFormType.Acord_20,
  AcordFormType.Acord_21,
  AcordFormType.Acord_22,
  AcordFormType.Acord_23,
  AcordFormType.Acord_24,
  AcordFormType.Acord_25,
  AcordFormType.Acord_27,
  AcordFormType.Acord_29,
  AcordFormType.Acord_30
];

// Section → form mapping, mirroring the backend `acordFormSectionForm` map so the
// rules editor only offers sections that belong to the selected form.
export const SECTIONS_BY_FORM: Record<AcordFormType, AcordFormSection[]> = {
  [AcordFormType.Acord_20]: [AcordFormSection.Acord_20Remarks],
  [AcordFormType.Acord_21]: [AcordFormSection.Acord_21Remarks],
  [AcordFormType.Acord_22]: [AcordFormSection.Acord_22Remarks],
  [AcordFormType.Acord_23]: [AcordFormSection.Acord_23Remarks],
  [AcordFormType.Acord_29]: [AcordFormSection.Acord_29Remarks],
  [AcordFormType.Acord_24]: [
    AcordFormSection.Acord_24LocationDescription,
    AcordFormSection.Acord_24SpecialConditions
  ],
  [AcordFormType.Acord_25]: [AcordFormSection.Acord_25DescriptionOfOperations],
  [AcordFormType.Acord_27]: [
    AcordFormSection.Acord_27PropertyInformation,
    AcordFormSection.Acord_27CoverageInformation,
    AcordFormSection.Acord_27Remarks
  ],
  [AcordFormType.Acord_28]: [],
  [AcordFormType.Acord_30]: [AcordFormSection.Acord_30DescriptionOfOperations]
};

export const sectionsForForm = (form: AcordFormType): AcordFormSection[] =>
  SECTIONS_BY_FORM[form] ?? [];

// Human-readable labels for the controls.
export const DOO_MODE_LABELS: Record<DescriptionOfOperationsMode, string> = {
  [DescriptionOfOperationsMode.AcordFigStrict]:
    'ACORD Standards (only Operations, Locations, and Vehicles)',
  [DescriptionOfOperationsMode.CaraDefault]: 'Rules Based',
  [DescriptionOfOperationsMode.Empty]: 'Empty'
};

export const PRODUCER_INFORMATION_SOURCE_LABELS: Record<AcordProducerInformationSource, string> = {
  [AcordProducerInformationSource.ConfiguredDefault]: 'Configured Producer Information',
  [AcordProducerInformationSource.CaraFromInstructions]:
    'Producer name & contact from workspace instructions'
};

// The number plus the form's published title, matching the generate-certificate
// picker so an agent sees the same name in both places.
export const FORM_LABELS: Record<AcordFormType, string> = {
  [AcordFormType.Acord_20]: 'ACORD 20 (Certificate of Aviation Liability Insurance)',
  [AcordFormType.Acord_21]: 'ACORD 21 (Certificate of Aircraft Insurance)',
  [AcordFormType.Acord_22]: 'ACORD 22 (Intermodal Interchange Certificate of Insurance)',
  [AcordFormType.Acord_23]: 'ACORD 23 (Vehicle or Equipment Certificate of Insurance)',
  [AcordFormType.Acord_29]: 'ACORD 29 (Evidence of Flood Insurance)',
  [AcordFormType.Acord_24]: 'ACORD 24 (Certificate of Property Insurance)',
  [AcordFormType.Acord_25]: 'ACORD 25 (Certificate of Liability Insurance)',
  [AcordFormType.Acord_27]: 'ACORD 27 (Evidence of Property Insurance)',
  [AcordFormType.Acord_28]: 'ACORD 28 (Evidence of Commercial Property Insurance)',
  [AcordFormType.Acord_30]: 'ACORD 30 (Certificate of Insurance, Garage)'
};

export const APPEND_TYPE_LABELS: Record<AcordAppendType, string> = {
  [AcordAppendType.AdditionalInsuredEndorsements]: 'Additional insured',
  [AcordAppendType.AdditionalInterestsLossPayees]: 'Additional interests / loss payees',
  [AcordAppendType.WaiverOfSubrogation]: 'Waiver of subrogation',
  [AcordAppendType.OtherReferencedEndorsements]: 'Other referenced endorsements',
  [AcordAppendType.DeclarationPagesAndSchedules]: 'Declaration pages and schedules',
  [AcordAppendType.NeverAppend]: 'Never append'
};

export const SECTION_LABELS: Record<AcordFormSection, string> = {
  [AcordFormSection.Acord_20Remarks]: 'Remarks',
  [AcordFormSection.Acord_21Remarks]: 'Remarks',
  [AcordFormSection.Acord_22Remarks]: 'Remarks',
  [AcordFormSection.Acord_23Remarks]: 'Remarks',
  [AcordFormSection.Acord_29Remarks]: 'Remarks',
  [AcordFormSection.Acord_25DescriptionOfOperations]: 'Description of Operations',
  [AcordFormSection.Acord_30DescriptionOfOperations]: 'Description of Operations',
  [AcordFormSection.Acord_24LocationDescription]: 'Location / Description',
  [AcordFormSection.Acord_24SpecialConditions]: 'Special Conditions',
  [AcordFormSection.Acord_27PropertyInformation]: 'Property Information',
  [AcordFormSection.Acord_27CoverageInformation]: 'Coverage Information',
  [AcordFormSection.Acord_27Remarks]: 'Remarks'
};

export const SECTION_DESCRIPTIONS: Record<AcordFormSection, string> = {
  [AcordFormSection.Acord_20Remarks]:
    'Writes to the ACORD 20 Remarks box. The coverage sections above it are structured cells the policy answers on its own.',
  [AcordFormSection.Acord_21Remarks]:
    'Writes to the ACORD 21 Description of Operations / Remarks box. The coverage sections above it are structured cells the policy answers on its own.',
  [AcordFormSection.Acord_22Remarks]:
    'Writes to the ACORD 22 Remarks box. The certificate holder is pre-printed, so remarks cannot redirect the certificate to another party.',
  [AcordFormSection.Acord_23Remarks]:
    'Writes to the ACORD 23 Remarks box. The certificate describes one vehicle or item, so remarks cannot carry a second one.',
  [AcordFormSection.Acord_29Remarks]:
    'Writes to the ACORD 29 Remarks box. The evidence covers one building, so remarks cannot carry a second one.',
  [AcordFormSection.Acord_25DescriptionOfOperations]:
    'Writes to the ACORD 25 Description of Operations box. Rules apply only in Rules Based mode.',
  [AcordFormSection.Acord_30DescriptionOfOperations]:
    'Writes to the ACORD 30 Description of Operations box. Rules apply only in Rules Based mode.',
  [AcordFormSection.Acord_24LocationDescription]:
    'Writes to the ACORD 24 Location / Description remarks box.',
  [AcordFormSection.Acord_24SpecialConditions]:
    'Writes to the ACORD 24 Special Conditions / Additional Remarks box.',
  [AcordFormSection.Acord_27PropertyInformation]:
    'Writes to the ACORD 27 Property Description field.',
  [AcordFormSection.Acord_27CoverageInformation]:
    'Guides optional ACORD 27 coverage-detail wording; policy-supported limits, deductibles, and form numbers remain required.',
  [AcordFormSection.Acord_27Remarks]: 'Writes to the ACORD 27 Remarks box.'
};

export interface RuleRow {
  // Omitted for rows the user just added; the server assigns one on save.
  id?: string;
  formType: AcordFormType;
  // Null for a form-level rule on a form with no scoped sections (e.g. ACORD 28).
  // The editor only authors section-scoped rules, but the persisted model permits
  // null, so we preserve it round-trip rather than dropping it.
  section: AcordFormSection | null;
  condition: string;
  phrasing: string;
  // Provenance only: a preset was pre-populated from current prompt language. The
  // server owns the flag by rule id; the editor uses it for the badge and read-only
  // preset controls.
  isPreset: boolean;
  // UI-only: presets are toggled on/off rather than deleted. Toggled-off presets are
  // omitted from the replace-all save payload; custom rules are always enabled and
  // are removed via the trash control instead.
  enabled: boolean;
}

export interface COIEOIState {
  descriptionOfOperationsMode: DescriptionOfOperationsMode;
  numberFormat: AcordNumberFormat;
  producerInformationSource: AcordProducerInformationSource;
  idCardLogoBase64Data: string | null;
  appendTypesByForm: Record<AppendFormType, AcordAppendType[]>;
  rules: RuleRow[];
}

type ACORDFormConfigurationFragment =
  AgencyFieldsFragment['configuration']['acordFormConfiguration'];

// Build editable component state from the persisted configuration. Every agency
// owns an explicit configuration (seeded at creation, backfilled for existing
// agencies), so the stored values are used directly. `defaults` (the
// defaultACORDFormConfiguration query result) provides scalar fallbacks and the
// canonical preset definitions used to render disabled presets. Each form is
// guaranteed an append entry (defaulting to NEVER_APPEND when absent) so the UI
// always renders one selector per form.
export const stateFromConfiguration = (
  config: ACORDFormConfigurationFragment,
  defaults: ACORDFormConfigurationFragment
): COIEOIState => {
  // List fields are agency-owned: null and empty both mean "none configured".
  // `defaults` is only used for scalar fallbacks and inactive preset definitions.
  const appendRules = config.appendRules ?? [];

  const appendTypesByForm = {} as Record<AppendFormType, AcordAppendType[]>;
  for (const form of APPEND_FORMS) {
    const rule = appendRules.find((r) => r.formType === form);
    // Drop categories the form cannot claim. A configuration saved while the
    // selector still offered every category to every form can hold one, and the
    // server now rejects it on write, so a stale value has to leave on load
    // rather than ride along in the next save.
    const relevant = (rule?.appendTypes ?? []).filter((type) =>
      appendTypeOptionsForForm(form).includes(type)
    );
    appendTypesByForm[form] = relevant.length > 0 ? relevant : [AcordAppendType.NeverAppend];
  }

  return {
    descriptionOfOperationsMode:
      config.descriptionOfOperationsMode ??
      defaults.descriptionOfOperationsMode ??
      DescriptionOfOperationsMode.CaraDefault,
    numberFormat: config.numberFormat ?? defaults.numberFormat ?? AcordNumberFormat.Commas,
    producerInformationSource:
      config.producerInformationSource ??
      defaults.producerInformationSource ??
      AcordProducerInformationSource.ConfiguredDefault,
    idCardLogoBase64Data: config.idCardLogoBase64Data ?? null,
    appendTypesByForm,
    rules: mergeRules(config.rules ?? null, defaults.rules ?? [])
  };
};

// Reconstruct the editable rule list from the agency's stored rules and the
// canonical preset definitions.
//
// Active stored rules preserve agency order and wording. Presets absent from the
// stored list are appended as disabled rows from defaults so they can be
// re-enabled without making null/empty stored lists implicitly active. Custom and
// unsupported stored rules round-trip even when the grouped editor does not
// render their form/section.
const mergeRules = (
  stored: NonNullable<ACORDFormConfigurationFragment['rules']> | null,
  defaults: NonNullable<ACORDFormConfigurationFragment['rules']>
): RuleRow[] => {
  const presetDefs = defaults.filter((r) => r.isPreset);
  const presetDefById = new Map(presetDefs.map((r) => [r.id, r]));
  const storedList = stored ?? [];
  const enabledPresetIds = new Set<string>();
  const rows: RuleRow[] = [];

  for (const r of storedList) {
    const presetDef = presetDefById.get(r.id);
    if (presetDef) {
      enabledPresetIds.add(r.id);
      rows.push({
        id: r.id,
        formType: presetDef.formType,
        section: presetDef.section ?? null,
        condition: r.condition,
        phrasing: r.phrasing,
        isPreset: true,
        enabled: true
      });
      continue;
    }

    rows.push({
      id: r.id,
      formType: r.formType,
      section: r.section ?? null,
      condition: r.condition,
      phrasing: r.phrasing,
      isPreset: false,
      enabled: true
    });
  }

  for (const def of presetDefs) {
    if (enabledPresetIds.has(def.id)) {
      continue;
    }
    rows.push({
      id: def.id,
      formType: def.formType,
      section: def.section ?? null,
      condition: def.condition,
      phrasing: def.phrasing,
      isPreset: true,
      enabled: false
    });
  }

  return rows;
};

// Reconcile a new MultiListbox selection against the previous one, enforcing the
// backend invariants: NEVER_APPEND is mutually exclusive with every other type,
// and an empty selection is disallowed (falls back to NEVER_APPEND).
export const reconcileAppendTypes = (
  prev: AcordAppendType[],
  next: AcordAppendType[]
): AcordAppendType[] => {
  if (next.length === 0) {
    return [AcordAppendType.NeverAppend];
  }
  const justAddedNever =
    next.includes(AcordAppendType.NeverAppend) && !prev.includes(AcordAppendType.NeverAppend);
  if (justAddedNever) {
    return [AcordAppendType.NeverAppend];
  }
  if (next.includes(AcordAppendType.NeverAppend) && next.length > 1) {
    return next.filter((t) => t !== AcordAppendType.NeverAppend);
  }
  return next;
};

// DOO-scoped rules are not applied when the DOO box is left to the strict ACORD
// FIG standard or kept empty, so the editor greys those rows out. ACORD 25 and
// ACORD 30 both have a Description of Operations box and share the one mode.
export const DOO_SECTIONS: AcordFormSection[] = [
  AcordFormSection.Acord_25DescriptionOfOperations,
  AcordFormSection.Acord_30DescriptionOfOperations
];

export const isDOOScopedRuleDisabled = (
  mode: DescriptionOfOperationsMode,
  section: AcordFormSection | null
): boolean =>
  section !== null &&
  DOO_SECTIONS.includes(section) &&
  (mode === DescriptionOfOperationsMode.AcordFigStrict ||
    mode === DescriptionOfOperationsMode.Empty);

// A blank custom rule scoped to the given form section. New rows carry no id (the
// server assigns one on save).
export const newRuleRow = (formType: AcordFormType, section: AcordFormSection): RuleRow => ({
  formType,
  section,
  condition: '',
  phrasing: '',
  isPreset: false,
  enabled: true
});

// Duplicate a preset into a fresh, editable custom rule (no id, isPreset false) so
// the agency can tailor the preset's wording without losing the original preset,
// which they can keep enabled, disable, or revert to at any time.
export const presetToCustomRow = (preset: RuleRow): RuleRow => ({
  formType: preset.formType,
  section: preset.section,
  condition: preset.condition,
  phrasing: preset.phrasing,
  isPreset: false,
  enabled: true
});

export interface RuleErrors {
  condition?: string;
  phrasing?: string;
}

// Validate editable custom rules. Presets and rules outside this editor's fixed
// form/section groups are skipped because the user cannot correct them here.
// Runtime-suppressed DOO custom rules remain validated because they are preserved
// in the save payload. Append invariants are enforced eagerly by
// reconcileAppendTypes. Errors are keyed by the global rule index so the editor
// can surface each one inline.
export const validateRules = (rules: RuleRow[]): Record<number, RuleErrors> => {
  const errors: Record<number, RuleErrors> = {};
  rules.forEach((rule, index) => {
    if (rule.isPreset) return;
    const visibleSections = sectionsForForm(rule.formType);
    if (
      rule.section === null ||
      !RULE_FORMS.includes(rule.formType) ||
      !visibleSections.includes(rule.section)
    ) {
      return;
    }
    const ruleError: RuleErrors = {};
    if (!rule.condition.trim()) ruleError.condition = 'A condition is required.';
    if (!rule.phrasing.trim()) ruleError.phrasing = 'Phrasing is required.';
    if (ruleError.condition || ruleError.phrasing) errors[index] = ruleError;
  });
  return errors;
};

// Build the replace-all mutation input. Existing rows keep their id; new rows omit
// it. Append entries are emitted for every form, order follows array position.
// Toggled-off presets are dropped: omitting a preset from the persisted list is how
// "disabled" is expressed. Runtime DOO suppression does not remove enabled rules.
export const buildConfigurationInput = (state: COIEOIState): AcordFormConfigurationInput => ({
  descriptionOfOperationsMode: state.descriptionOfOperationsMode,
  numberFormat: state.numberFormat,
  producerInformationSource: state.producerInformationSource,
  idCardLogoBase64Data: state.idCardLogoBase64Data || null,
  appendRules: APPEND_FORMS.map((form) => ({
    formType: form,
    appendTypes:
      state.appendTypesByForm[form] && state.appendTypesByForm[form].length > 0
        ? state.appendTypesByForm[form]
        : [AcordAppendType.NeverAppend]
  })),
  rules: state.rules
    .filter((rule) => !rule.isPreset || rule.enabled)
    .map((rule) => ({
      ...(rule.id ? { id: rule.id } : {}),
      formType: rule.formType,
      section: rule.section,
      condition: rule.condition.trim(),
      phrasing: rule.phrasing.trim()
    }))
});

// Strip the `data:<mime>;base64,` prefix from a FileReader data URL, leaving raw
// base64 (matching how authorizedRepSignatureBase64Data is stored).
export const dataUrlToBase64 = (dataUrl: string): string =>
  dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl;

// Infer the image mime type from the leading bytes encoded in the stored base64,
// so the preview renders with the correct data URL. The persisted value carries
// no mime prefix, so we sniff the two accepted types (PNG base64 begins with
// `iVBOR`, JPEG with `/9j/`) and default to PNG.
export const base64ImageMime = (base64: string): string =>
  base64.startsWith('/9j/') ? 'image/jpeg' : 'image/png';

// Accepted ID-card logo image types, max upload size, and max pixel dimensions.
// The logo is stored inline as base64 on the agency configuration (and carried
// into ACORD 50 generation), so these limits bound how much every read of the
// agency carries. The stamp is placed in the larger whitespace on the right of
// the ID card, so a higher-resolution source is worthwhile — but we still cap it
// to keep the inline payload reasonable.
export const LOGO_ACCEPTED_TYPES = ['image/png', 'image/jpeg'];
export const LOGO_MAX_BYTES = 1024 * 1024; // 1 MB
export const LOGO_MAX_DIMENSION = 1024; // px, applied to both width and height

// Validate a chosen logo file's type and size, returning an error message or null
// when valid. Pixel dimensions are checked separately by validateLogoDimensions,
// which must decode the image asynchronously.
export const validateLogoFile = (file: File): string | null => {
  if (!LOGO_ACCEPTED_TYPES.includes(file.type)) {
    return 'Logo must be a PNG or JPG image.';
  }
  if (file.size > LOGO_MAX_BYTES) {
    return 'Logo must be 1 MB or smaller.';
  }
  return null;
};

// Decode the image off-DOM to validate its intrinsic dimensions, resolving to an
// error message or null when valid. Resolves with an error if the image cannot be
// decoded so a corrupt file can't slip through.
export const validateLogoDimensions = (file: File): Promise<string | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.naturalWidth > LOGO_MAX_DIMENSION || img.naturalHeight > LOGO_MAX_DIMENSION) {
        resolve(`Logo must be ${LOGO_MAX_DIMENSION}×${LOGO_MAX_DIMENSION} pixels or smaller.`);
      } else {
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve('Logo image could not be read. Please choose a PNG or JPG file.');
    };
    img.src = url;
  });
